import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  BookOpen, 
  Clock, 
  Users,
  Target
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';

// Available courses for template creation
const AVAILABLE_COURSES = [
  { id: 'html-css-fundamentals', title: 'HTML & CSS Fundamentals', category: 'Frontend' },
  { id: 'javascript-basics', title: 'JavaScript Basics', category: 'Frontend' },
  { id: 'react-basics', title: 'React Fundamentals', category: 'Frontend' },
  { id: 'programming-basics', title: 'Programming Fundamentals', category: 'Foundation' },
  { id: 'git-version-control', title: 'Git & Version Control', category: 'Foundation' },
  { id: 'sql-fundamentals', title: 'SQL Fundamentals', category: 'Backend' },
  { id: 'node-js-basics', title: 'Node.js Basics', category: 'Backend' },
  { id: 'python-programming', title: 'Python Programming', category: 'Backend' }
];

interface AssignmentTemplate {
  id: string;
  name: string;
  description: string;
  courses: string[];
  estimatedDuration: string;
  difficulty: string;
  targetRole?: string;
  department?: string;
}

interface AssignmentTemplatesManagerProps {
  templates: AssignmentTemplate[];
  onQuickAssign: (templateId: string, userIds: string[]) => void;
}

const AssignmentTemplatesManager: React.FC<AssignmentTemplatesManagerProps> = ({
  templates,
  onQuickAssign
}) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AssignmentTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<Partial<AssignmentTemplate>>({
    name: '',
    description: '',
    courses: [],
    estimatedDuration: '',
    difficulty: 'Beginner',
    targetRole: '',
    department: ''
  });

  const handleCreateTemplate = () => {
    if (!newTemplate.name || !newTemplate.description || !newTemplate.courses?.length) {
      toast.error('Please fill in all required fields');
      return;
    }

    // In real implementation, this would save to database
    toast.success('Template created successfully');
    setIsCreateDialogOpen(false);
    setNewTemplate({
      name: '',
      description: '',
      courses: [],
      estimatedDuration: '',
      difficulty: 'Beginner',
      targetRole: '',
      department: ''
    });
  };

  const handleEditTemplate = (template: AssignmentTemplate) => {
    setSelectedTemplate(template);
    setNewTemplate(template);
    setIsEditDialogOpen(true);
  };

  const handleDeleteTemplate = (templateId: string) => {
    // In real implementation, this would delete from database
    toast.success('Template deleted successfully');
  };

  const getCoursesByIds = (courseIds: string[]) => {
    return AVAILABLE_COURSES.filter(course => courseIds.includes(course.id));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Assignment Templates</h3>
          <p className="text-sm text-muted-foreground">
            Manage reusable course bundles for quick assignments
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="truncate">{template.name}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditTemplate(template)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteTemplate(template.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {template.description}
              </p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <BookOpen className="h-4 w-4" />
                  <span>{template.courses.length} courses</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>{template.estimatedDuration}</span>
                </div>
                {template.targetRole && (
                  <div className="flex items-center gap-2 text-sm">
                    <Target className="h-4 w-4" />
                    <span>{template.targetRole}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-1 mb-4">
                <Badge variant="outline">{template.difficulty}</Badge>
                {template.department && (
                  <Badge variant="secondary">{template.department}</Badge>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Courses Included:</Label>
                <div className="space-y-1">
                  {getCoursesByIds(template.courses).slice(0, 3).map((course) => (
                    <div key={course.id} className="text-xs text-muted-foreground truncate">
                      • {course.title}
                    </div>
                  ))}
                  {template.courses.length > 3 && (
                    <div className="text-xs text-muted-foreground">
                      + {template.courses.length - 3} more courses
                    </div>
                  )}
                </div>
              </div>

              <Button 
                className="w-full mt-4" 
                variant="outline"
                onClick={() => onQuickAssign(template.id, [])}
              >
                <Users className="h-4 w-4 mr-2" />
                Use Template
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Template Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Assignment Template</DialogTitle>
            <DialogDescription>
              Create a reusable template for course assignments
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={newTemplate.name || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Frontend Fundamentals"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-description">Description *</Label>
              <Input
                id="template-description"
                value={newTemplate.description || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the learning path"
              />
            </div>

            <div className="space-y-2">
              <Label>Difficulty Level</Label>
              <Select 
                value={newTemplate.difficulty} 
                onValueChange={(value) => setNewTemplate(prev => ({ ...prev, difficulty: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated-duration">Estimated Duration</Label>
              <Input
                id="estimated-duration"
                value={newTemplate.estimatedDuration || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                placeholder="e.g., 8 weeks"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="target-role">Target Role (Optional)</Label>
              <Input
                id="target-role"
                value={newTemplate.targetRole || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, targetRole: e.target.value }))}
                placeholder="e.g., Frontend Developer"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate}>
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Assignment Template</DialogTitle>
            <DialogDescription>
              Modify the template settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-template-name">Template Name *</Label>
              <Input
                id="edit-template-name"
                value={newTemplate.name || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-template-description">Description *</Label>
              <Input
                id="edit-template-description"
                value={newTemplate.description || ''}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Difficulty Level</Label>
              <Select 
                value={newTemplate.difficulty} 
                onValueChange={(value) => setNewTemplate(prev => ({ ...prev, difficulty: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              // In real implementation, this would update the database
              toast.success('Template updated successfully');
              setIsEditDialogOpen(false);
            }}>
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentTemplatesManager;